import React, { useState, useMemo } from 'react';
import { CreditCard, DollarSign, Shield, Clock } from 'lucide-react';
import { format } from 'date-fns';

import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { serviceCategories, serviceInstructions } from '../../config/services';
import { useWizard } from '../../Contexts/WizardContext';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { createSetupIntent, findOrCreateStripeCustomer, getPaymentSecretPayNow, scheduleAppointmentsPaidNow, scheduleAppointmentsPayLater, scheduleAppointmentsPromoCode, verifyPaymentIntentStatus } from '../../api/endpoints';
import { Button } from '../General/Button';
import Loader from '../General/Loader';
import { formatDateToUSA } from '../../config/utils';

export const services = serviceCategories.flatMap(category => category.services)

export default function PaymentMethod() {
  // -------- Var Block Start --------------
  const [searchParams] = useSearchParams();
  const isInternal = searchParams.get('isInternal');

  const { updateFormData, previousStep, formData, nextStep } = useWizard();
  
  const [paymentOption, setPaymentOption] = useState(formData.paymentMethod || 'now');
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState(null);
  const stripe = useStripe();
  const elements = useElements();

  const [isChecked, setIsChecked] = useState(false)
  const [cardComplete, setCardComplete] = useState(false)

  const allAppintments = formData.appointments

  const allSubervicesSelected = formData.serviceInstances.filter(i => i.status === 'scheduled' && i.appointmentDate)
  const totalPrice = allSubervicesSelected.map(item => item.price).reduce((a,b) => a+b, 0)

  // -------- Tips --------
  const TIP_PRESETS = [10, 15, 20]
  const [tipType, setTipType] = useState('percent')
  const [tipInput, setTipInput] = useState('')

  // what the user typed, sanitized: percent is capped at 100, fixed is a dollar amount.
  // rounded to 2 decimals so the tip we display and the tip_amount we send always agree
  const tipValue = useMemo(() => {
    const parsed = parseFloat(tipInput)
    if (isNaN(parsed) || parsed <= 0) return 0
    const rounded = Math.round(parsed * 100) / 100
    return tipType === 'percent' ? Math.min(rounded, 100) : rounded
  }, [tipInput, tipType])

  // integer cents, so percent tips never drift on float math
  const tipAmountCents = useMemo(() => {
    if (!tipValue) return 0
    return tipType === 'percent'
      ? Math.round(totalPrice * tipValue) // totalPrice * 100 * (tipValue / 100)
      : Math.round(tipValue * 100)
  }, [tipValue, tipType, totalPrice])

  const totalWithTipCents = Math.round(totalPrice * 100) + tipAmountCents

  // backend contract: fixed -> dollars (50), percent -> fraction (0.05)
  const tipPayload = {
    tip_type: tipType,
    tip_amount: tipType === 'percent' ? Number((tipValue / 100).toFixed(4)) : tipValue
  }

  const formatCents = (cents) => (cents / 100).toFixed(2)

  const handleTipTypeChange = (type) => {
    setTipType(type)
    setTipInput('')
  }

  // clamp at entry so the field never shows a value different from the tip we charge
  const handleTipInputChange = (raw) => {
    const parsed = parseFloat(raw)
    if (raw !== '' && !isNaN(parsed)) {
      if (parsed < 0) return setTipInput('')
      if (tipType === 'percent' && parsed > 100) return setTipInput('100')
    }
    setTipInput(raw)
  }



  const soldByOptions = [
    'George',
    'Jackie',
    'Jason',
    'Jonathan',
    'Marlon',
    'Nikki',
    'Emily',
  ]
  const [selectedSoldBy, setSelectedSoldBy] = useState('George')


  // -------- Var Block End --------------

  // -------- Handler Block Start --------------
  const payNowHandler = async () => {
    const cardElement = elements.getElement(CardElement);

    let customerId 
    try {
      customerId = await findOrCreateStripeCustomer({email: formData.contactData.email, name: `${formData.contactData.firstName} ${formData.contactData.lastName}`}) 
    } catch(e) {
      setCardError(e.message)
      return
    }
    const {clientSecret, paymentIntentId} = await getPaymentSecretPayNow({amount: totalWithTipCents, capture_method: 'automatic', customerId})
    let { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
            card: cardElement,
        }
    });
    
    if (error) {
      if (error.type === 'invalid_request_error') {
        // Could be the duplicate confirm case — verify server-side before showing error
        const isVerified = await verifyPaymentIntentStatus(paymentIntentId);
  
        if (isVerified) {
          await handleSuccessfulPaymentPayNow(paymentIntentId);
          return;
        }
      }
      setCardError(error.message);
    } else {
      await handleSuccessfulPaymentPayNow(paymentIntent.id)
    }
  }

  const handleSuccessfulPaymentPayNow = async (paymentIntentId) => {
    try {
      const scheduleResponse = await scheduleAppointmentsPaidNow({
        appointments: formData.appointments,
        contactData: formData.contactData,
        address: formData.address,
        serviceArea: formData.jobsiteData.service_area.service_area,
        paymentIntentId,
        sold_by: isInternal ? selectedSoldBy : '',
        ...tipPayload
      })

      if (scheduleResponse.status === 'success') {
        nextStep();
      } else {
        setCardError('Something is wrong with the payment. Please contact support.')
      }
    } catch(e) {
      setCardError(e.message);
    }
  }

  const payLaterFlow = async () => {
    let customerId, clientSecret
    let errorCustomer = false, status = 'success'
    try {
      customerId = await findOrCreateStripeCustomer({email: formData.contactData.email, name: `${formData.contactData.firstName} ${formData.contactData.lastName}`}) 
    } catch(e) {
      status = 'Stripe Customer failed.'
      errorCustomer = e.message
    }
    
    try {
      const response = await createSetupIntent({customerId})
      clientSecret = response.clientSecret
    } catch(e) {
      status = 'Stripe SetupIntent failed.'
      errorCustomer = e.message
    }
    return { clientSecret, errorCustomer, status }
  }

  const payLaterHandler = async () => {
    const cardElement = elements.getElement(CardElement);
    const { clientSecret, errorCustomer, status } = await payLaterFlow()
    if (status == 'success') { 
      try {
        const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: `${formData.contactData.firstName} ${formData.contactData.lastName}`,
              email: formData.contactData.email,
            },
          },
        });
        
        if (error) {
          console.error(error.message);
          setCardError('Something is wrong with card confirmation.', error.message)
        } else if (setupIntent.status === 'succeeded') {
          const paymentMethodId = setupIntent.payment_method
          console.log('setupIntent success, to create aapt')
          console.log('paymentMethodId', paymentMethodId)
  
          const scheduleResponse = await scheduleAppointmentsPayLater({
            appointments: allAppintments, 
            contactData: formData.contactData, 
            address: formData.address, 
            serviceArea: formData.jobsiteData.service_area.service_area,
            paymentMethodId: paymentMethodId,
            sold_by: isInternal ? selectedSoldBy : '',
            ...tipPayload
          })
          if (scheduleResponse.status == 'success') {
            // navigate(`/thank-you-work/${paymentMethodId}`);
            nextStep()
          } else {
            setCardError('Something is wrong with scheduling. Please, contact support to get details.')
          }
        }
      } catch(e) {
        setCardError(`${e.message}`)
      }
    } else {
      setCardError('Something is wrong with card confirmation.', `${status} ${errorCustomer}`)
    }

  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }
    setIsProcessing(true)
    updateFormData(oldVal => {
      return {
        ...oldVal,
        paymentOption,
        tip: { ...tipPayload, tip_total: tipAmountCents / 100 }
      }
    })
    // ---- to debug: to proceed w/o payment handlers ----
    // nextStep()
    // ---- to debug: to proceed w/o payment handlers ----

    if (paymentOption === 'now') {
      console.log('pay now branhc')
      await payNowHandler()
    } else {
      console.log('pay later branch')
      await payLaterHandler()
    }
    setIsProcessing(false)
  };

  const handlePromocodeSchedule = async () => {
    setIsProcessing(true)
    const scheduleResponse = await scheduleAppointmentsPromoCode({
      appointments: allAppintments, 
      contactData: formData.contactData, 
      address: formData.address, 
      serviceArea: formData.jobsiteData.service_area.service_area,
      sold_by: isInternal ? selectedSoldBy : '',
      promo_code: promoCode
    })
    console.log('scheduleResponse', scheduleResponse)
    nextStep()
  }

  // -------- Handler Block End --------------

  // -------- Templates Block Start --------------
  const serviceSummary = () => {   
    if (allAppintments.length === 0) {
        return (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <DollarSign className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900 mb-1">Service Summary</p>
                <p className="text-blue-700">No services scheduled</p>
              </div>
            </div>
          </div>
        );
    }
    
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex gap-3">
          <div className="text-sm flex-1">
            <p className="font-medium text-blue-900 mb-2">Service Summary</p>
            <div className="space-y-3 text-left">
              {allAppintments.map(appt => {
                  return (
                    <div key={appt.selectedSlot.id} className='flex'>
                      <p className="text-blue-800 font-medium">
                        {formatDateToUSA(appt.appointmentDate)} - {appt.appointmentTime === 'full_day' ? 'Full Day' : appt.appointmentTime}
                      </p>
                      <p className="text-blue-700 ml-3">
                        {appt.services.map((inst, idx) => 
                            `${inst.serviceName}${idx == appt.services.length - 1 ? '.' : ', '}`
                        )}
                      </p>
                      <p className="text-blue-800 font-medium ml-auto">
                        ${appt.app_price}
                      </p>
                    </div>
                  );
                })}
              </div>
              {paymentOption === 'now' && (
                <div className="border-t border-blue-200 mt-3 pt-2 text-right">
                  {tipAmountCents > 0 && (
                    <>
                      <p className="text-blue-800">Subtotal: ${totalPrice}</p>
                      <p className="text-blue-800">
                        Tip{tipType === 'percent' ? ` (${tipValue}%)` : ''}: ${formatCents(tipAmountCents)}
                      </p>
                    </>
                  )}
                  <p className="text-blue-900 font-semibold">Total: ${formatCents(totalWithTipCents)}</p>
                </div>
              )}
              {paymentOption === 'later' && tipAmountCents > 0 && (
                <div className="border-t border-blue-200 mt-3 pt-2 text-right">
                  <p className="text-blue-800">
                    Tip: {tipType === 'percent' ? `${tipValue}%` : `$${formatCents(tipAmountCents)}`}
                  </p>
                </div>
              )}
              {paymentOption === 'now' && (
                <p className="text-green-600 text-sm mt-1 text-right">
                  Payment will be processed immediately
                </p>
              )}
              {paymentOption === 'later' && (
                <p className="text-green-600 text-sm mt-1 text-right">
                  Card will be charged after each service completion
                </p>
              )}
              {paymentOption === 'later' && tipType === 'fixed' && tipAmountCents > 0 && allAppintments.length > 1 && (
                <p className="text-green-600 text-sm mt-1 text-right">
                  Tips will be charged once after the first service completion
                </p>
              )}
            </div>
          </div>
        </div>
      );
  }

  const tipsBlock = () => {
    const activePreset = tipType === 'percent' ? tipValue : null

    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <DollarSign className="w-5 h-5 text-green-600" />
          <h3 className="font-medium text-gray-700">Add a Tip</h3>
          <span className="text-xs uppercase tracking-wide text-gray-400">Optional</span>
        </div>
        <p className="text-xs text-gray-500 mb-4">100% of your tip goes to the crew.</p>

        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 mb-4">
          {[{ id: 'percent', label: 'Percent' }, { id: 'fixed', label: 'Fixed amount' }].map(option => (
            <button
              key={option.id}
              type="button"
              onClick={() => handleTipTypeChange(option.id)}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${tipType === option.id
                ? 'bg-white text-gray-800 shadow-sm font-medium'
                : 'text-gray-500 hover:text-gray-700'}`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {tipType === 'percent' && (
          <div className="flex flex-wrap gap-2 mb-3">
            {TIP_PRESETS.map(preset => (
              <button
                key={preset}
                type="button"
                onClick={() => setTipInput(String(preset))}
                className={`px-4 py-2 text-sm rounded-lg border transition-colors ${activePreset === preset
                  ? 'border-green-600 bg-green-50 text-green-700 font-medium'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >
                {preset}%
              </button>
            ))}
            {tipValue > 0 && (
              <button
                type="button"
                onClick={() => setTipInput('')}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-500 hover:border-gray-300"
              >
                No tip
              </button>
            )}
          </div>
        )}

        <div className="relative w-40">
          {tipType === 'fixed' && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
          )}
          <input
            type="number"
            min="0"
            step={tipType === 'percent' ? '1' : '0.01'}
            max={tipType === 'percent' ? '100' : undefined}
            value={tipInput}
            onChange={(e) => handleTipInputChange(e.target.value)}
            placeholder={tipType === 'percent' ? 'Custom %' : '0.00'}
            className={`w-full border border-gray-200 rounded-lg py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${tipType === 'fixed' ? 'pl-7 pr-3' : 'pl-3 pr-7'}`}
          />
          {tipType === 'percent' && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
          )}
        </div>

        {tipAmountCents > 0 && (
          <p className="mt-3 text-sm text-gray-700">
            {paymentOption === 'now'
              ? `Tip: $${formatCents(tipAmountCents)}`
              : tipType === 'percent'
                ? `A ${tipValue}% tip will be added to your charges.`
                : `A $${formatCents(tipAmountCents)} tip will be added to your charges.`}
          </p>
        )}
      </div>
    )
  }

  const promocodeBlock = () => {
    return (
      <>
      <div class="bg-white border border-gray-200 rounded-xl p-5 mb-4">

        <label class="flex items-center gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            id="use-promo"
            class="w-4 h-4 rounded accent-blue-600 cursor-pointer"
            onClick={(e) => {
              setUsePromocode(e.target.checked)
              // the CardElement is unmounted/remounted with the payment block, so its state resets
              setCardComplete(false)
            }}
          />
          <span class="text-sm text-gray-800">Use a promo code instead of payment</span>
        </label>

        <div id="promo-section" class="mt-4 pt-4 border-t border-gray-100">
          <label class="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
            Promo code
          </label>
          <div class="flex gap-2">
            <input
              type="text"
              placeholder="e.g. SUMMER25"
              class="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => setPromoCode(e.target.value)}
            />
            <button 
              class="px-4 py-2 text-sm border border-gray-200 rounded-lg bg-green-600 text-white hover:bg-green-700 transition disabled:bg-gray-200 disabled:text-gray-50"
              disabled={!usePromocode}
              onClick={() => handlePromocodeSchedule()}
            >
              Apply
            </button>
          </div>
        </div>

        </div>
      </>
    )
  }

  const [usePromocode, setUsePromocode] = useState(false)
  const [promoCode, setPromoCode] = useState('')
  const mainTemplate = () => {
    const isContinueDisabled = isProcessing || usePromocode || !cardComplete || !isChecked
    const continueTooltip = (isProcessing || usePromocode)
      ? ''
      : !cardComplete
        ? 'Please fill in your payment details to continue'
        : !isChecked
          ? 'Please agree with terms and conditions to continue'
          : ''

    return (
      <>
        {isInternal && promocodeBlock()}
        {usePromocode ? '' : paymentDetails()}
        {!usePromocode && allAppintments.length > 0 && tipsBlock()}
        {serviceSummary()}

        {isInternal && 
          <div className="flex flex-col w-full gap-3 p-4 bg-white rounded-lg border border-gray-200 shadow-sm mb-8">
            <select
              value={selectedSoldBy}
              onChange={(e) => setSelectedSoldBy(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {soldByOptions.map(option => {
                return (
                  <option key={option} value={option}>{option}</option>
                )
              })}
            </select>
          </div>
        }

      <div className="flex flex-col gap-4">
        <label className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${isChecked
                        ? "bg-green-50 border-green-300"
                        : "bg-gray-50 border-gray-200"}`}>
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
            className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:opacity-50"
          />
          <span className="text-sm font-medium text-gray-500">I read and agree with <a target="_blank" href="https://acalandscape.com/terms/" className='text-blue-500'>Terms and conditions</a></span>
        </label>

        <div className="flex gap-4">
            <Button
              label='Back'
              isDisabled={false}
              onClick={previousStep}
            />
            <Button
              label='Continue'
              isDisabled={isContinueDisabled}
              tooltip={continueTooltip}
              onClick={handleSubmit}
              primary
            />
        </div>
      </div>
      </>
    )
  }

  const paymentDetails = () => {
    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <button
          onClick={() => setPaymentOption('now')}
          className={`p-4 rounded-lg border-2 transition-all text-left ${paymentOption === 'now'
            ? "border-green-600 bg-green-50"
            : "border-gray-200 hover:border-gray-300"}`}
        >
          <div className="flex items-start gap-3">
            <CreditCard className={`w-6 h-6 mt-1 ${paymentOption === 'now' ? "text-green-600" : "text-gray-400"}`} />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 mb-1">Pay Now</h3>
              <p className="text-sm text-gray-600">
                Pay now to get it taken care of and off your mind.
              </p>
            </div>
            {paymentOption === 'now' && (
              <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
        </button>

        <button
          onClick={() => setPaymentOption('later')}
          className={`p-4 rounded-lg border-2 transition-all text-left ${paymentOption === 'later'
            ? "border-green-600 bg-green-50"
            : "border-gray-200 hover:border-gray-300"}`}
        >
          <div className="flex items-start gap-3">
            <Clock className={`w-6 h-6 mt-1 ${paymentOption === 'later' ? "text-green-600" : "text-gray-400"}`} />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 mb-1">Pay Later</h3>
              <p className="text-sm text-gray-600">
                Card required to reserve appointment. Charged after service completion.
              </p>
            </div>
            {paymentOption === 'later' && (
              <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
        </button>
        </div>

        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h3 className="font-medium text-gray-700 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-600" />
            {paymentOption === 'now' ? 'Secure Payment Information' : 'Card Required for Reservation'}
          </h3>
          {paymentOption === 'later' && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <p className="text-sm text-blue-800">
                Your card will be securely stored but <strong>not charged</strong> until after service completion.
              </p>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Card Information
              </label>
              <div className="p-4 border border-gray-300 rounded-lg">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#374151',
                        '::placeholder': {
                          color: '#9CA3AF',
                        },
                      },
                    },
                  }}
                  onChange={(event) => {
                    setCardComplete(event.complete);
                    if (event.error) {
                      setCardError(event.error.message);
                    } else {
                      setCardError(null);
                    }
                  }}
                />
              </div>
              {cardError && (
                <p className="mt-1 text-sm text-red-600">{cardError}</p>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-4 flex items-center gap-1">
            <Shield className="w-4 h-4" />
            Secured by Stripe - Your payment information is encrypted and secure
          </p>
        </div>
      </>
    )
  }

  return (
    <div className="w-full max-w-2xl mx-auto p-4">

      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
        Confirm Your Booking
        </h2>
        <p className="text-gray-600">Choose how you'd like to pay for your service</p>
      </div>
      <Loader isLoading={isProcessing} >
        {mainTemplate()}
      </Loader>
    </div>
  );
};